// src/hooks/usePriceAnalyzer.js
import { useState, useCallback } from 'react';

export const usePriceAnalyzer = () => {
  const [symbols, setSymbols] = useState([]);
  const [analysisCount, setAnalysisCount] = useState(0);
  const [analysisHistory, setAnalysisHistory] = useState([]);
  const [currentAnalysisId, setCurrentAnalysisId] = useState(0);
  const [loading, setLoading] = useState(false);
  const [currentKlineData, setCurrentKlineData] = useState(null);

  const baseUrl = 'https://fapi.binance.com';

  const init = useCallback(async () => {
    console.log('📊 Initializing Price Analyzer...');
    await loadSymbols();
    loadAnalysisHistory();
  }, []);

  const loadSymbols = useCallback(async () => {
    try {
      const response = await fetch(`${baseUrl}/fapi/v1/exchangeInfo`);
      const data = await response.json();
      
      const symbolList = data.symbols
        .filter(s => s.quoteAsset === 'USDT' && s.status === 'TRADING' && s.contractType === 'PERPETUAL')
        .map(s => ({
          symbol: s.symbol,
          baseAsset: s.baseAsset,
          volume: 0
        }))
        .sort((a, b) => a.symbol.localeCompare(b.symbol));

      setSymbols(symbolList);
      console.log(`✅ Loaded ${symbolList.length} trading pairs`);
      
    } catch (error) {
      console.error('❌ Failed to load symbols:', error);
      throw error;
    }
  }, []);

  const loadAnalysisHistory = useCallback(() => {
    try {
      const stored = localStorage.getItem('binance_analysis_history');
      if (stored) {
        const history = JSON.parse(stored);
        setAnalysisHistory(history);
        setAnalysisCount(history.filter(a => a.success).length);
      }
    } catch (error) {
      console.error('Failed to load analysis history:', error);
    }
  }, []);

  const saveAnalysisHistory = useCallback((history) => {
    try {
      localStorage.setItem('binance_analysis_history', JSON.stringify(history));
    } catch (error) {
      console.error('Failed to save analysis history:', error);
    }
  }, []);

  const completeAnalysis = useCallback((symbol, success = true) => {
    if (success) {
      const newHistory = [...analysisHistory, {
        symbol,
        timestamp: Date.now(),
        success: true
      }];
      setAnalysisHistory(newHistory);
      setAnalysisCount(prev => prev + 1);
      saveAnalysisHistory(newHistory);
    }
  }, [analysisHistory, saveAnalysisHistory]);

  const getKlineData = useCallback(async (symbol, interval = '15m', limit = 1000) => {
    const url = `${baseUrl}/fapi/v1/klines?symbol=${symbol}&interval=${interval}&limit=${limit}`;
    
    try {
      console.log(`🔍 Fetching kline data for ${symbol}...`);
      
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: Invalid symbol or API error`);
      }
      
      const data = await response.json();
      if (!data || data.length === 0) {
        throw new Error('No data returned from API');
      }

      console.log(`✅ Fetched ${data.length} kline data points for ${symbol}`);

      const processedData = data.map(item => ({
        openTime: parseInt(item[0]),
        open: parseFloat(item[1]),
        high: parseFloat(item[2]),
        low: parseFloat(item[3]),
        close: parseFloat(item[4]),
        volume: parseFloat(item[5]),
        closeTime: parseInt(item[6]),
        quoteAssetVolume: parseFloat(item[7]),
        numberOfTrades: parseInt(item[8])
      }));

      setCurrentKlineData(processedData);
      return processedData;
    } catch (error) {
      throw new Error(`Data fetch failed: ${error.message}`);
    }
  }, []);

  const performAnalysis = useCallback(async (symbol, days, timezone) => {
    try {
      setLoading(true);
      
      // Calculate how many 15-minute intervals we need
      const limit = Math.min(days * 96, 1500); // 96 intervals per day (24h * 4)
      const klineData = await getKlineData(symbol, '15m', limit);
      
      // Process the kline data to find daily extremes
      const dailyData = processDailyExtremes(klineData, timezone);
      
      // Generate analysis results
      const analysisResults = generateAnalysisResults(dailyData, symbol, days);
      
      // Mark analysis as complete
      completeAnalysis(symbol, true);
      
      return analysisResults;
      
    } catch (error) {
      console.error('Analysis failed:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [getKlineData, completeAnalysis]);

  const processDailyExtremes = useCallback((klineData, timezoneOffset) => {
    console.log(`📅 Processing ${klineData.length} candles with timezone offset UTC${timezoneOffset >= 0 ? '+' : ''}${timezoneOffset}`);
    
    const dailyExtremes = new Map();
    
    klineData.forEach((candle) => {
      // Convert to specified timezone
      const date = new Date(candle.openTime + (timezoneOffset * 60 * 60 * 1000));
      const dateKey = date.toISOString().split('T')[0]; // YYYY-MM-DD format
      
      if (!dailyExtremes.has(dateKey)) {
        dailyExtremes.set(dateKey, {
          date: dateKey,
          high: candle.high,
          low: candle.low,
          highTime: date.getHours() * 60 + date.getMinutes(),
          lowTime: date.getHours() * 60 + date.getMinutes(),
          candles: []
        });
      }
      
      const dayData = dailyExtremes.get(dateKey);
      dayData.candles.push({
        ...candle,
        localTime: date.getHours() * 60 + date.getMinutes(),
        localDate: date
      });
      
      // Update daily high
      if (candle.high > dayData.high) {
        dayData.high = candle.high;
        dayData.highTime = date.getHours() * 60 + date.getMinutes();
      }
      
      // Update daily low
      if (candle.low < dayData.low) {
        dayData.low = candle.low;
        dayData.lowTime = date.getHours() * 60 + date.getMinutes();
      }
    });
    
    const result = Array.from(dailyExtremes.values()).sort((a, b) => a.date.localeCompare(b.date));
    console.log(`📊 Processed ${result.length} days of data`);
    
    return result;
  }, []);

  const generateAnalysisResults = useCallback((dailyData, symbol, days) => {
    // Calculate time distribution for highs and lows
    const highTimes = dailyData.map(d => d.highTime);
    const lowTimes = dailyData.map(d => d.lowTime);
    
    // Create hourly distribution
    const highDistribution = createHourlyDistribution(highTimes);
    const lowDistribution = createHourlyDistribution(lowTimes);
    
    // Calculate statistics
    const stats = {
      totalDays: dailyData.length,
      avgHighTime: calculateAverageTime(highTimes),
      avgLowTime: calculateAverageTime(lowTimes),
      mostFrequentHighHour: findMostFrequentHour(highTimes),
      mostFrequentLowHour: findMostFrequentHour(lowTimes),
      priceRange: {
        highest: Math.max(...dailyData.map(d => d.high)),
        lowest: Math.min(...dailyData.map(d => d.low))
      }
    };
    
    return {
      symbol,
      days,
      dailyData,
      highDistribution,
      lowDistribution,
      stats,
      generatedAt: new Date().toISOString()
    };
  }, []);

  const createHourlyDistribution = useCallback((times) => {
    const distribution = new Array(24).fill(0);
    
    times.forEach(timeInMinutes => {
      const hour = Math.floor(timeInMinutes / 60);
      distribution[hour]++;
    });
    
    return distribution;
  }, []);

  const calculateAverageTime = useCallback((times) => {
    const avgMinutes = times.reduce((sum, time) => sum + time, 0) / times.length;
    const hours = Math.floor(avgMinutes / 60);
    const minutes = Math.round(avgMinutes % 60);
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  }, []);

  const findMostFrequentHour = useCallback((times) => {
    const hourCounts = new Array(24).fill(0);
    
    times.forEach(timeInMinutes => {
      const hour = Math.floor(timeInMinutes / 60);
      hourCounts[hour]++;
    });
    
    const maxCount = Math.max(...hourCounts);
    const mostFrequentHour = hourCounts.indexOf(maxCount);
    
    return {
      hour: mostFrequentHour,
      count: maxCount,
      percentage: ((maxCount / times.length) * 100).toFixed(1)
    };
  }, []);

  const refreshData = useCallback(async () => {
    await loadSymbols();
  }, [loadSymbols]);

  return {
    symbols,
    analysisCount,
    analysisHistory,
    currentKlineData,
    loading,
    init,
    loadSymbols,
    completeAnalysis,
    getKlineData,
    performAnalysis,
    refreshData
  };
};